import { Box, Button, Dialog } from '@veracity/vui'
import { useMemo, useState } from 'react'

import { TreeSelect } from '@/components'
import { useCurrentUser } from '@/hooks'

import { useDownloadResults } from '../hooks'
import { DownloadResultsSectionNames } from '../types'

interface Props {
  scenarioId: number
}

export const ScenarioResultsDownloadResultsDialog = ({ scenarioId }: Props) => {
  const [isVisible, setIsVisible] = useState(false)
  const { isInternal } = useCurrentUser()

  const [selectedSections, setSelectedSections] = useState<
    DownloadResultsSectionNames[]
  >([])

  const { isDownloading, downloadFile } = useDownloadResults(
    scenarioId,
    selectedSections
  )

  const options = useMemo(() => {
    const baseOptions = [
      DownloadResultsSectionNames.CASH_FLOWS,
      DownloadResultsSectionNames.INCOMES,
      DownloadResultsSectionNames.COSTS,
      DownloadResultsSectionNames.OPERATIONS
    ]

    return isInternal
      ? [...baseOptions, DownloadResultsSectionNames.RAW_RESULTS]
      : baseOptions
  }, [isInternal])

  const getLabel = (option: DownloadResultsSectionNames) => {
    switch (option) {
      case DownloadResultsSectionNames.CASH_FLOWS:
        return 'Cash Flows'
      case DownloadResultsSectionNames.INCOMES:
        return 'Incomes'
      case DownloadResultsSectionNames.COSTS:
        return 'Costs'
      case DownloadResultsSectionNames.OPERATIONS:
        return 'Operations'
      case DownloadResultsSectionNames.RAW_RESULTS:
        return 'Raw Results'
    }
  }

  const areAllOptionsSelected = selectedSections.length === options.length

  const handleClose = () => {
    if (isDownloading) return
    setIsVisible(false)
    resetAllOptions(false)
  }

  const handleChange = (
    isChecked: boolean,
    type: DownloadResultsSectionNames
  ) =>
    setSelectedSections(state => {
      const filteredState = state.filter(x => x !== type)
      return isChecked ? [...filteredState, type] : filteredState
    })

  const setAllOperationsOptions = (isChecked: boolean) => {
    options.forEach(option => handleChange(isChecked, option))
  }

  const resetAllOptions = (isChecked: boolean) =>
    setAllOperationsOptions(isChecked)

  const handleDownload = () =>
    downloadFile(undefined, { onSuccess: handleClose })

  return (
    <>
      <Button size="lg" onClick={() => setIsVisible(true)}>
        Download Results
      </Button>

      <Dialog
        isOpen={isVisible}
        title="Download Results"
        onClose={handleClose}
        cancelButton={{ text: 'Cancel', disabled: isDownloading }}
        submitButton={{
          text: 'Download',
          onClick: handleDownload,
          isLoading: isDownloading,
          disabled: !selectedSections.length || isDownloading
        }}
        body={
          <Box column p={2} pl={4}>
            <Box justify="right">
              <Button
                size="lg"
                variant="tertiaryDark"
                disabled={isDownloading}
                iconLeft={areAllOptionsSelected ? 'falTimes' : 'falCheck'}
                onClick={() => resetAllOptions(!areAllOptionsSelected)}>
                Select all
              </Button>
            </Box>

            <TreeSelect
              isDisabled={isDownloading}
              items={[
                {
                  label: 'Dispatch results (.zip)',
                  onChange: setAllOperationsOptions,
                  children: options.map(option => ({
                    label: getLabel(option),
                    checked: selectedSections.includes(option),
                    onChange: isChecked => handleChange(isChecked, option)
                  }))
                }
              ]}
            />
          </Box>
        }
      />
    </>
  )
}
